'use client'

import React, { forwardRef } from 'react'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import {
  userMenuItemIconStyles,
  userMenuItemLabelStyles,
  userMenuItemStyles,
} from './UserMenu.styles'
import type { UserMenuItem } from './UserMenu.types'

export type UserMenuRowProps = {
  item: UserMenuItem
  colorScheme?: ColorScheme
  /** Drives `aria-expanded`; only meaningful on a row that has a submenu. */
  isSubmenuOpen?: boolean
  /** Fired on click, Enter or Space. */
  onActivate: () => void
  /**
   * The four props `next/link` (`legacyBehavior` + `passHref`) injects when it
   * clones this row. Declared explicitly rather than as an open prop bag so
   * the router contract this component has to honour is visible here.
   */
  href?: string
  onClick?: (event: React.MouseEvent) => void
  onMouseEnter?: (event: React.MouseEvent) => void
  onTouchStart?: (event: React.TouchEvent) => void
  /**
   * Defaults to -1: a `role="menu"` is one stop in the tab sequence, and its
   * rows are reached with the arrow keys while `UserMenu` moves focus. Rows
   * left at 0 would each become their own tab stop, which is the composite
   * pattern ARIA explicitly rules out.
   */
  tabIndex?: number
}

/**
 * One row of a {@link UserMenu} — leading icon plus label. Sits inside the
 * menu; not meant to be used standalone.
 *
 * Forwards its ref and any extra props onto the underlying element because
 * consumers wrap rows in a router link: `next/link` with `legacyBehavior`
 * + `passHref` clones this element and injects `href`, `onClick` and `ref`.
 * Dropping those would silently downgrade client-side navigation to a full
 * page load, so the injected `onClick` is composed with this row's own
 * handler rather than overwritten by it.
 */
export const UserMenuRow = forwardRef<HTMLElement, UserMenuRowProps>(
  (
    {
      item,
      colorScheme = 'dark',
      isSubmenuOpen = false,
      onActivate,
      href,
      onClick: injectedOnClick,
      onMouseEnter,
      onTouchStart,
      tabIndex = -1,
    },
    ref
  ) => {
    const isAnchor = Boolean(item.href || item.path)
    const hasSubmenu = Boolean(item.submenu?.length)

    // A `path` row carries its href directly, so it is a real link even
    // before a router wrapper injects one; an injected href wins if present.
    const routerProps = {
      ...(isAnchor ? { href: href ?? item.href ?? item.path } : {}),
      ...(item.href ? { rel: 'noopener noreferrer', target: '_blank' } : {}),
      ...(onMouseEnter ? { onMouseEnter } : {}),
      ...(onTouchStart ? { onTouchStart } : {}),
    }

    const activate = (event: React.MouseEvent | React.KeyboardEvent) => {
      // A submenu row is a disclosure, not a navigation.
      if (hasSubmenu) {
        event.preventDefault()
        event.stopPropagation()
      }
      onActivate()
    }

    return (
      <Box
        {...userMenuItemStyles(colorScheme)}
        {...routerProps}
        aria-expanded={hasSubmenu ? isSubmenuOpen : undefined}
        aria-haspopup={hasSubmenu ? 'menu' : undefined}
        as={isAnchor ? 'a' : 'div'}
        data-testid={item['data-testid']}
        onClick={(event: React.MouseEvent) => {
          activate(event)
          // Runs after this row's own handler so a wrapping router link still
          // navigates, matching how next/link composes onto a plain anchor.
          injectedOnClick?.(event)
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
          if (event.key !== 'Enter' && event.key !== ' ') return

          if (isAnchor) {
            if (event.key !== ' ') return
            event.preventDefault()
            event.currentTarget.click()
            return
          }

          event.preventDefault()
          activate(event)
        }}
        ref={ref}
        role='menuitem'
        tabIndex={tabIndex}
      >
        {item.icon && (
          <Box as='span' {...userMenuItemIconStyles()}>
            {item.icon}
          </Box>
        )}
        <Box as='span' {...userMenuItemLabelStyles()}>
          {item.label}
        </Box>
      </Box>
    )
  }
)

UserMenuRow.displayName = 'UserMenuRow'
